import { defineStore } from 'pinia'
import { ref } from 'vue'
import client from '../api/client.js'

export const useAdsStore = defineStore('ads', () => {
  const campaigns = ref([])
  const loading = ref(false)

  /**
   * payload matches CampaignCreateRequest on avocash-ads:
   *   { advertiserId, name, bidModel, bidAmountCfa, dailyBudgetCfa,
   *     totalBudgetCfa, targeting: TargetingDto, creative: AdCreativeDto }
   */
  async function createCampaign(payload) {
    const { data } = await client.post('/api/v1/campaigns', payload)
    campaigns.value.unshift(data)
    return data
  }

  async function activate(campaignId) {
    await client.post(`/api/v1/campaigns/${campaignId}/activate`)
  }

  async function pause(campaignId) {
    await client.post(`/api/v1/campaigns/${campaignId}/pause`)
  }

  async function fetchStats(advertiserId) {
    loading.value = true
    try {
      const { data } = await client.get('/api/v1/campaigns/stats', {
        params: { advertiserId },
      })
      campaigns.value = data
      return data
    } finally {
      loading.value = false
    }
  }

  /**
   * endpoint: 'category-partner' | 'group-intelligence'
   * payload matches CategoryPartnerRequest / GroupIntelligenceRequest
   */
  async function createSubscription(endpoint, payload) {
    const { data } = await client.post(`/api/v1/subscriptions/${endpoint}`, payload)
    return data
  }

  /** AdminAdController creative endpoints */
  async function setCreativeStatus(creativeId, status) {
    await client.put(`/api/admin/ads/creatives/${creativeId}/status`, { status })
  }

  async function activateCreative(creativeId) {
    await client.put(`/api/admin/ads/creatives/${creativeId}/activate`)
  }

  async function deactivateCreative(creativeId) {
    await client.put(`/api/admin/ads/creatives/${creativeId}/deactivate`)
  }

  async function setCreativeTitle(creativeId, title) {
    await client.put(`/api/admin/ads/creatives/${creativeId}/title`, { title })
  }

  async function setCreativeBody(creativeId, body) {
    await client.put(`/api/admin/ads/creatives/${creativeId}/body`, { body })
  }

  async function setCreativeImage(creativeId, image) {
    await client.put(`/api/admin/ads/creatives/${creativeId}/image`, image)
  }

  async function setCreativeCta(creativeId, cta) {
    await client.put(`/api/admin/ads/creatives/${creativeId}/cta`, { cta })
  }

  async function setCreativeFormat(creativeId, format) {
    await client.put(`/api/admin/ads/creatives/${creativeId}/format`, { format })
  }

  /** AdminAdController campaign endpoints */
  async function setCampaignName(campaignId, name) {
    await client.put(`/api/admin/ads/campaigns/${campaignId}/name`, { name })
  }

  async function setCampaignBudget(campaignId, budget) {
    await client.put(`/api/admin/ads/campaigns/${campaignId}/budget`, { budget })
  }

  async function setCampaignSpentBudget(campaignId, spentBudget) {
    await client.put(`/api/admin/ads/campaigns/${campaignId}/spent-budget`, { spentBudget })
  }

  async function setCampaignBid(campaignId, bid) {
    await client.put(`/api/admin/ads/campaigns/${campaignId}/bid`, { bid })
  }

  async function setCampaignDates(campaignId, startDate, endDate) {
    await client.put(`/api/admin/ads/campaigns/${campaignId}/dates`, { startDate, endDate })
  }

  async function setCampaignFrequencyCap(campaignId, frequencyCap) {
    await client.put(`/api/admin/ads/campaigns/${campaignId}/frequency-cap`, { frequencyCap })
  }

  /** AdCampaignController endpoints */
  async function createAdCampaign(payload) {
    const { data } = await client.post('/v1/adCampaigns', payload)
    return data
  }

  async function activateAdCampaign(campaignId) {
    await client.post('/v1/adCampaigns/{id}/activate'.replace('{id}', campaignId))
  }

  async function pauseAdCampaign(campaignId) {
    await client.post('/v1/adCampaigns/{id}/pause'.replace('{id}', campaignId))
  }

  async function submitAdCampaign(campaignId) {
    await client.post('/v1/adCampaigns/{id}/submit'.replace('{id}', campaignId))
  }

  async function approveAdCampaign(campaignId) {
    await client.post('/v1/adCampaigns/{id}/approve'.replace('{id}', campaignId))
  }

  async function rejectAdCampaign(campaignId) {
    await client.post('/v1/adCampaigns/{id}/reject'.replace('{id}', campaignId))
  }

  async function fetchAdCampaignStats(advertiserId) {
    loading.value = true
    try {
      const { data } = await client.get('/v1/adCampaigns/stats', {
        params: { advertiserId },
      })
      return data
    } catch (e) { throw e } finally { loading.value = false }
  }

  async function fetchAudienceEstimate(payload) {
    loading.value = true
    try {
      const { data } = await client.post('/v1/adCampaigns/audience-estimate', payload)
      return data
    } catch (e) { throw e } finally { loading.value = false }
  }

  /** EventCosponsorshipController */
  async function touchCosponsorship(campaignId, eventId, touchpoint) {
    await client.post(`/v1/adCampaigns/${campaignId}/cosponsorships/${eventId}/touchpoints/${touchpoint}`)
  }

  /** GroupActivationController */
  async function fetchCampaignActivations(campaignId) {
    loading.value = true
    try {
      const { data } = await client.get(`/v1/adCampaigns/${campaignId}/activations`)
      return data
    } catch (e) { throw e } finally { loading.value = false }
  }

  async function fetchActivationSummary(campaignId) {
    loading.value = true
    try {
      const { data } = await client.get(`/v1/adCampaigns/${campaignId}/activations/summary`)
      return data
    } catch (e) { throw e } finally { loading.value = false }
  }

  return { campaigns, loading, createCampaign, activate, pause, fetchStats, createSubscription, setCreativeStatus, activateCreative, deactivateCreative, setCreativeTitle, setCreativeBody, setCreativeImage, setCreativeCta, setCreativeFormat, setCampaignName, setCampaignBudget, setCampaignSpentBudget, setCampaignBid, setCampaignDates, setCampaignFrequencyCap, createAdCampaign, activateAdCampaign, pauseAdCampaign, submitAdCampaign, approveAdCampaign, rejectAdCampaign, fetchAdCampaignStats, fetchAudienceEstimate, touchCosponsorship, fetchCampaignActivations, fetchActivationSummary }
})
